import { computed } from 'vue'
import { freezeObject } from '@/global/utils/utils'

const TAB_KEY = 'documents'
// The key the rows are held under, and the one the sync endpoint expects them in.
const ITEMS_KEY = 'documents'

export function useDocuments (options) {
  const {
    trans,
    route,
    dynamicTableComponent
  } = options || {}

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: `products/${route.params.id}/documents`
    },
    post: {
      module: 'road-maintenance',
      route: `products/${route.params.id}/documents`,
      spoofMethod: 'PUT',
      // With files to upload the request is a POST carrying multipart form data
      // and the real method is spoofed through `_method`. Without them it goes
      // as a JSON PUT - which is the only way to tell `sync` that the list is
      // now empty, since form data cannot carry an empty array.
      sendJsonWithoutFiles: true
    }
  }))

  const globalStyle = freezeObject({
    useFixedHeaderWidth: true,
    dataRowHeight: '64px'
  })

  // Both `index` and `sync` answer with a bare list of documents, while the
  // table reads its rows from a property - so the list is placed under the key
  // the table and the sync endpoint share. Doing it here rather than from a
  // post-fetch hook keeps the saved rows in `originalFormData` too, which is
  // what cancelling an edit restores.
  const transformResponseData = (data) => ({
    [ITEMS_KEY]: Array.isArray(data) ? data : []
  })

  // Each entry is reduced to what `sync` validates: an existing `id`, or a new
  // `file`, plus the `name`. Rows come back from `index` carrying `url` too,
  // and a row added in the table carries null placeholders for every column -
  // neither belongs in the request.
  //
  // `originalData` rather than `copiedData`: the latter is deep-cloned, and a
  // File does not survive that.
  const modifyDataBeforeSend = (event) => {
    const { originalData } = event || {}
    const rows = Array.isArray(originalData?.[ITEMS_KEY]) ? originalData[ITEMS_KEY] : []

    return {
      [ITEMS_KEY]: rows.map(row => {
        const entry = {
          name: row?.name ?? ''
        }

        if (row?.id) {
          entry.id = row.id
        }

        if (row?.file instanceof File) {
          entry.file = row.file
        }

        return entry
      })
    }
  }

  return computed(() => ({
    title: trans('fleet.travel_documents'),
    key: TAB_KEY,
    apiConfig: apiConfig.value,
    transformResponseData,
    modifyDataBeforeSend,
    tabContent: [
      {
        component: dynamicTableComponent,
        title: trans('road-maintenance/inventory.edit_documents'),
        type: 'dynamic-columns',
        globalStyle,
        showButtons: true,
        showBackButton: {
          uiViewName: 'TemporaryRegulationsProducts'
        },
        key: ITEMS_KEY,
        cellsConfig: {
          name: {
            style: {
              textAlign: 'left'
            }
          },
          // One file per row: `sync` takes each entry as either an existing
          // `id` or a new `file`, so there is no separate list of deleted ids -
          // removing the row is what deletes the document.
          file: {
            type: 'file',
            multiple: false,
            previewKey: 'url',
            // The endpoint takes any file type (only a 200MB size cap), and the
            // preview lists documents by name with a download action rather
            // than rendering them - so nothing here has to be an image.
            accept: '',
            max: 1,
            previewTitle: trans('fleet.travel_documents')
          }
        },
        headers: [
          {
            key: 'id',
            label: trans('road-maintenance/settings.id'),
            editable: false,
            style: {
              width: '80px'
            }
          },
          {
            key: 'file',
            label: trans('road-maintenance/inventory.images'),
            editable: false,
            style: {
              width: '180px'
            }
          },
          {
            key: 'name',
            label: trans('road-maintenance/inventory.document_name'),
            style: {
              width: '100%'
            }
          }
        ],
        removeRowConfig: {
          removable: true
        }
      }
    ]
  }))
}
